/**
 * Order status badges on purchases / sales / order details.
 * Pending only means live checkout hold; unpaid Cancelled → Payment not completed.
 */

const STATUS_FALLBACKS: Record<string, string> = {
    Pending: 'Pending',
    Paid: 'Paid',
    Processing: 'Processing',
    Shipped: 'Shipped',
    InTransit: 'In transit',
    Delivered: 'Delivered',
    Completed: 'Completed',
    Cancelled: 'Cancelled',
    Refunded: 'Refunded',
    Problem: 'Problem',
};

export type OrderStatusDisplayOpts = {
    isViewerSeller?: boolean;
    isLiveCheckout?: boolean;
    isPaymentNotCompleted?: boolean;
    paymentStatus?: string | null;
};

export function orderStatusTranslationKey(status: string | null | undefined): string {
    const value = String(status ?? '').trim();
    return value ? `order.status.${value}` : 'order.status';
}

export function orderStatusFallback(status: string | null | undefined): string {
    const value = String(status ?? '').trim();
    if (!value) return '';
    if (STATUS_FALLBACKS[value]) return STATUS_FALLBACKS[value];
    return value.replace(/([a-z])([A-Z])/g, '$1 $2');
}

function isUnpaidCancel(opts?: OrderStatusDisplayOpts): boolean {
    if (opts?.isPaymentNotCompleted) return true;
    const pay = String(opts?.paymentStatus ?? '');
    return pay === 'Failed' || pay === 'Pending';
}

/**
 * Resolve shop order status label via i18n + English fallback.
 * Role-aware for live Pending and unpaid Cancelled.
 */
export function translateOrderStatus(
    tr: (key: string, fallback: string) => string,
    status: string | null | undefined,
    opts?: OrderStatusDisplayOpts
): string {
    const value = String(status ?? '').trim();
    if (!value) return '';

    if (value === 'Pending') {
        // Hold window elapsed but worker has not cancelled yet
        if (opts?.isLiveCheckout === false) {
            return tr('order.status.PaymentNotCompleted', 'Payment not completed');
        }
        if (opts?.isViewerSeller) {
            return tr(
                'order.status.Pending.seller',
                'Reserved — checkout in progress'
            );
        }
        return tr('order.status.Pending.buyer', 'Awaiting payment');
    }

    if (value === 'Cancelled' && isUnpaidCancel(opts)) {
        return tr('order.status.PaymentNotCompleted', 'Payment not completed');
    }

    if (value === 'Paid' && opts?.isViewerSeller) {
        return tr('order.status.Paid.seller', 'Paid — ship now');
    }

    if (value === 'Paid' && !opts?.isViewerSeller) {
        return tr('order.status.Paid.buyer', 'Paid — waiting to ship');
    }

    return tr(orderStatusTranslationKey(value), orderStatusFallback(value));
}

/** Active list: live holds + in-progress paid orders (not history terminals). */
export function isActivePurchaseOrder(o: {
    status?: string | null;
    isLiveCheckout?: boolean;
}): boolean {
    const s = String(o.status ?? '');
    if (s === 'Pending') return !!o.isLiveCheckout;
    return (
        s === 'Paid' ||
        s === 'Processing' ||
        s === 'Shipped' ||
        s === 'InTransit' ||
        s === 'Problem'
    );
}

export function isHistoryPurchaseOrder(o: {
    status?: string | null;
    isLiveCheckout?: boolean;
}): boolean {
    return !isActivePurchaseOrder(o);
}
